import { useState, type ReactNode } from 'react';
import { C } from '@ridgewill/brand';
import { adminApi, type SignInBan, type SuspendedNetwork } from '../lib/api.js';
import { useAction, useAsync } from '../lib/useAsync.js';
import { InfoBubble, Screen } from '../components/Screen.js';
import { Select } from '../components/Select.js';
import {
  AsyncBoundary,
  Badge,
  Button,
  EmptyState,
  OutcomeNote,
  Panel,
  PanelBody,
  Table,
  Td,
  When,
  inputStyle,
} from '../components/ui.js';

/**
 * Support → Suspended IPs (auth#587, Jon 2026-10-04): every network barred
 * from sign-in, where it is, how hard it has tried, and a way for a Support
 * Admin to bar a network for as long as they decide.
 *
 * The data is auth's (`/admin/signin-bans`), not the support service's: the
 * bans live where sign-in checks them. Two kinds share one row per network:
 *
 *   - an OWNER ban, from "This wasn't me": that network may not sign in to
 *     that one account, for 14 days. Neighbours on the address are unaffected.
 *   - a STAFF ban: nobody on that network may sign in, until the chosen time.
 *
 * `#/support-configuration/suspended-ips` is the list, `#/support-configuration/suspended-ips/<network>`
 * one network. Banning and lifting are offered only when the server says so
 * (`canBan`, which is `user:ban`): a Support Agent sees the list and nothing
 * to press.
 */
export const suspendedIpsPath = (network?: string) =>
  network ? `/support-configuration/suspended-ips/${encodeURIComponent(network)}` : '/support-configuration/suspended-ips';

const PAGE = 50;

/** A path segment as the router hands it over, decoded; a malformed one is used as is. */
export function decodeSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

export function SuspendedIpsScreen({ tabs, navigate }: { tabs: ReactNode; navigate: (to: string) => void }) {
  const [offset, setOffset] = useState(0);
  const [banning, setBanning] = useState(false);
  const { state, reload } = useAsync(() => adminApi.signInBans({ limit: PAGE, offset }), [offset]);
  const canBan = state.status === 'ready' && state.data.canBan;

  return (
    <Screen
      filters={tabs}
      actions={
        <>
          {canBan ? <Button onClick={() => setBanning((b) => !b)}>Ban an IP address</Button> : null}
          <Button onClick={() => reload()}>Refresh</Button>
        </>
      }
    >
      {banning ? (
        <BanForm
          onCancel={() => setBanning(false)}
          onBanned={(network) => {
            setBanning(false);
            navigate(suspendedIpsPath(network));
          }}
        />
      ) : null}
      <AsyncBoundary state={state}>
        {({ total, networks }) => (
          <Panel title={`Suspended IP addresses (${total})`}>
            {networks.length ? (
              <>
                <Table
                  columns={['Address', 'Location', 'Blocked from', 'Failed attempts', 'Refused since', 'Last attempt', 'Until']}
                >
                  {networks.map((n) => (
                    <NetworkRow key={n.network} n={n} />
                  ))}
                </Table>
                {total > PAGE ? (
                  <div style={s.pager}>
                    <Button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>
                      Previous
                    </Button>
                    <span style={{ color: C.muted, fontSize: '.85rem' }}>
                      {offset + 1}–{Math.min(offset + PAGE, total)} of {total}
                    </span>
                    <Button disabled={offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)}>
                      Next
                    </Button>
                  </div>
                ) : null}
              </>
            ) : (
              <EmptyState
                title="No IP addresses are suspended"
                suggestion="An address appears here when an account owner reports failed sign-ins that were not them, or when a Support Admin bans it."
              />
            )}
          </Panel>
        )}
      </AsyncBoundary>
    </Screen>
  );
}

function NetworkRow({ n }: { n: SuspendedNetwork }) {
  return (
    <tr>
      <Td mono>
        <a href={`#${suspendedIpsPath(n.network)}`} style={s.link}>
          {n.address}
        </a>
      </Td>
      <Td>{n.location ?? <span style={{ color: C.muted }}>Unknown</span>}</Td>
      <Td>
        <Scope wholeNetwork={n.wholeNetwork} accounts={n.accounts} />
      </Td>
      <Td>{n.failedAttempts}</Td>
      <Td>{n.blockedAttempts}</Td>
      <Td>{n.lastAttemptAt ? <When iso={n.lastAttemptAt} /> : <span style={{ color: C.muted }}>None since</span>}</Td>
      <Td>
        <Until iso={n.until} />
      </Td>
    </tr>
  );
}

function Scope({ wholeNetwork, accounts }: { wholeNetwork: boolean; accounts: number }) {
  if (wholeNetwork) return <Badge tone="danger">Every account</Badge>;
  return <Badge tone="warn">{accounts === 1 ? '1 account' : `${accounts} accounts`}</Badge>;
}

function Until({ iso }: { iso: string }) {
  return (
    <span style={{ whiteSpace: 'nowrap' }} title={new Date(iso).toLocaleString()}>
      {new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
    </span>
  );
}

// ── One network ───────────────────────────────────────────────────────

export function SuspendedIpDetailScreen({
  network,
  tabs,
  navigate,
}: {
  network: string;
  tabs: ReactNode;
  navigate: (to: string) => void;
}) {
  // The router hands segments over still encoded (lib/route.ts splits the raw
  // hash), and a network key carries `:` and, for IPv6, `/`. Asking auth for
  // `ip%3A203.0.113.10` found nothing and rendered an empty network.
  const key = decodeSegment(network);
  const { state, reload } = useAsync(() => adminApi.signInBanNetwork(key), [key]);
  const [banning, setBanning] = useState(false);
  const action = useAction();

  return (
    <Screen filters={tabs} actions={<Button onClick={() => reload()}>Refresh</Button>}>
      <a href={`#${suspendedIpsPath()}`} style={s.back}>
        ← Suspended IPs
      </a>
      <AsyncBoundary state={state}>
        {({ address, bans, canBan }) => {
          const location = bans.find((b) => b.location)?.location ?? null;
          const wholeNetwork = bans.some((b) => b.scope === 'network');
          const failed = bans.reduce((sum, b) => sum + b.failedAttempts, 0);
          const refused = bans.reduce((sum, b) => sum + b.blockedAttempts, 0);
          const last = bans
            .map((b) => b.lastAttemptAt)
            .filter((t): t is string => !!t)
            .sort()
            .pop();
          return (
            <>
              <Panel
                title={address}
                action={
                  canBan ? (
                    <Button onClick={() => setBanning((b) => !b)}>
                      {wholeNetwork ? 'Change the ban' : 'Ban every account on it'}
                    </Button>
                  ) : null
                }
              >
                <PanelBody>
                  <div style={s.facts}>
                    <Fact label="Location">{location ?? 'Unknown'}</Fact>
                    <Fact label="Blocked from">
                      {bans.length ? (
                        <Scope wholeNetwork={wholeNetwork} accounts={bans.filter((b) => b.scope === 'account').length} />
                      ) : (
                        'Nothing'
                      )}
                    </Fact>
                    <Fact label="Failed attempts before a ban">{failed}</Fact>
                    <Fact label="Sign-ins refused since">{refused}</Fact>
                    <Fact label="Last attempt">{last ? <When iso={last} /> : 'None since'}</Fact>
                  </div>
                  {!bans.length ? <p style={{ margin: 0, color: C.muted }}>No ban is in force on this address now.</p> : null}
                </PanelBody>
              </Panel>

              {banning ? (
                <BanForm
                  address={address}
                  onCancel={() => setBanning(false)}
                  onBanned={() => {
                    setBanning(false);
                    reload(true);
                  }}
                />
              ) : null}

              {bans.length ? (
                <Panel title="Bans in force">
                  <Table columns={['Blocks', 'Set by', 'Failed attempts', 'Refused since', 'Since', 'Until', '']} actionsLast>
                    {bans.map((b) => (
                      <BanRow
                        key={b.key}
                        b={b}
                        canLift={canBan}
                        lifting={action.running}
                        onLift={() =>
                          void action
                            .run(async () => {
                              await adminApi.liftSignInBan(b.key);
                              return 'Lifted.';
                            })
                            .then((ok) => {
                              if (!ok) return;
                              if (bans.length === 1) navigate(suspendedIpsPath());
                              else reload(true);
                            })
                        }
                      />
                    ))}
                  </Table>
                  <PanelBody>
                    <OutcomeNote outcome={action.outcome && !action.outcome.ok ? action.outcome : null} />
                  </PanelBody>
                </Panel>
              ) : null}
            </>
          );
        }}
      </AsyncBoundary>
    </Screen>
  );
}

function BanRow({ b, canLift, lifting, onLift }: { b: SignInBan; canLift: boolean; lifting: boolean; onLift: () => void }) {
  const who = (p: SignInBan['account']) => p?.name || p?.email || 'A deleted account';
  return (
    <tr>
      <Td>
        {b.scope === 'network' ? (
          'Every account'
        ) : b.account ? (
          <a href={`#/users/${encodeURIComponent(b.account.id)}`} style={s.link}>
            {who(b.account)}
          </a>
        ) : (
          'One account'
        )}
      </Td>
      <Td>
        {b.source === 'staff' ? (
          <>Staff{b.bannedBy ? `: ${who(b.bannedBy)}` : ''}</>
        ) : (
          'The account owner'
        )}
      </Td>
      <Td>{b.scope === 'account' ? b.failedAttempts : <span style={{ color: C.muted }}>Not counted</span>}</Td>
      <Td>{b.blockedAttempts}</Td>
      <Td>
        <When iso={b.createdAt} />
      </Td>
      <Td>
        <Until iso={b.until} />
      </Td>
      <Td action>
        {canLift ? (
          <Button disabled={lifting} onClick={onLift}>
            Lift
          </Button>
        ) : null}
      </Td>
    </tr>
  );
}

// ── Banning ───────────────────────────────────────────────────────────

type Length = '1h' | '24h' | '7d' | '30d' | '90d' | '365d' | 'custom';
const LENGTHS: { value: Length; label: string; ms?: number }[] = [
  { value: '1h', label: '1 hour', ms: 3600_000 },
  { value: '24h', label: '24 hours', ms: 24 * 3600_000 },
  { value: '7d', label: '7 days', ms: 7 * 24 * 3600_000 },
  { value: '30d', label: '30 days', ms: 30 * 24 * 3600_000 },
  { value: '90d', label: '90 days', ms: 90 * 24 * 3600_000 },
  { value: '365d', label: '1 year', ms: 365 * 24 * 3600_000 },
  { value: 'custom', label: 'Until a date…' },
];

/** The end the form would send, or why it cannot. Exported for its test. */
export function banEnd(length: Length, customDate: string, now = Date.now()): { until: string } | { problem: string } {
  const preset = LENGTHS.find((l) => l.value === length)?.ms;
  if (preset) return { until: new Date(now + preset).toISOString() };
  if (!customDate) return { problem: 'Choose a date.' };
  // A date input gives midnight UTC; end of that day where the person is reads as meant.
  const end = new Date(`${customDate}T23:59:00`);
  if (Number.isNaN(end.getTime()) || end.getTime() <= now + 60_000) return { problem: 'Choose a date after today.' };
  if (end.getTime() > now + 365 * 24 * 3600_000) return { problem: 'A ban can last a year at most.' };
  return { until: end.toISOString() };
}

function BanForm({
  address,
  onCancel,
  onBanned,
}: {
  address?: string;
  onCancel: () => void;
  onBanned: (network: string) => void;
}) {
  const [ip, setIp] = useState(address ?? '');
  const [length, setLength] = useState<Length>('24h');
  const [customDate, setCustomDate] = useState('');
  const action = useAction();

  const submit = () => {
    const end = banEnd(length, customDate);
    void action.run(async () => {
      if ('problem' in end) throw new Error(end.problem);
      const ban = await adminApi.banSignInNetwork(ip.trim(), end.until);
      onBanned(ban.network);
      return 'Banned.';
    });
  };

  return (
    <Panel
      title={address ? `Ban every account on ${address}` : 'Ban an IP address'}
      info={
        <InfoBubble label="What a ban does">
          Nobody on this address can sign in with a password until the ban ends, whichever account they try. People
          already signed in, and sign-in with a passkey or Google, are not affected. A shared address, like a phone
          carrier or an office, can be many people.
        </InfoBubble>
      }
    >
      <PanelBody>
        <form
          style={s.form}
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <label style={s.field}>
            <span style={s.factLabel}>IP address</span>
            <input
              value={ip}
              onChange={(e) => setIp(e.target.value)}
              disabled={!!address}
              placeholder="203.0.113.4"
              aria-label="IP address"
              style={{ ...inputStyle, fontFamily: 'ui-monospace, monospace', minWidth: '16rem' }}
            />
          </label>
          <label style={s.field}>
            <span style={s.factLabel}>For</span>
            <Select<Length> value={length} onChange={setLength} label="Ban length" options={LENGTHS} />
          </label>
          {length === 'custom' ? (
            <label style={s.field}>
              <span style={s.factLabel}>Until the end of</span>
              <input
                type="date"
                value={customDate}
                onChange={(e) => setCustomDate(e.target.value)}
                aria-label="Ban end date"
                style={inputStyle}
              />
            </label>
          ) : null}
          <div style={{ display: 'flex', gap: '.4rem', alignItems: 'flex-end' }}>
            <Button tone="danger" disabled={action.running || !ip.trim()} onClick={submit}>
              Ban
            </Button>
            <Button onClick={onCancel}>Cancel</Button>
          </div>
        </form>
        <OutcomeNote outcome={action.outcome && !action.outcome.ok ? action.outcome : null} />
      </PanelBody>
    </Panel>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div style={s.factLabel}>{label}</div>
      <div>{children}</div>
    </div>
  );
}

const s = {
  link: { color: C.brand, textDecoration: 'none' },
  back: { color: C.brand, textDecoration: 'none', fontSize: '.88rem' },
  facts: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(11rem, 1fr))', gap: '.9rem 1.2rem' },
  factLabel: { fontSize: '.75rem', fontWeight: 600, color: C.muted, marginBottom: '.2rem' },
  pager: { display: 'flex', gap: '.6rem', alignItems: 'center', justifyContent: 'flex-end', padding: '.7rem 1.2rem' },
  form: { display: 'flex', gap: '.8rem', flexWrap: 'wrap', alignItems: 'flex-end' },
  field: { display: 'grid', gap: '.1rem' },
} as const;
